import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Tab,
  Tabs,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Fab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { workOrdersApi } from '../../services/api';
import { WorkOrderTimelineWidget } from '../WorkOrders';
import { ContractorPerformanceWidget } from '../ContractorManagement';
import { useNavigate } from 'react-router-dom';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`dashboard-tabpanel-${index}`}
    aria-labelledby={`dashboard-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const StatCard = ({ title, value, subtitle, icon, color = 'primary', trend = null }) => (
  <Card>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.main`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        {trend && (
          <Box sx={{ textAlign: 'center' }}>
            {trend}
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const ManagerDashboard = () => {
  const { user, canAccess } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Dashboard data
  const [workOrderStats, setWorkOrderStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (canAccess(['manager'])) {
      fetchDashboardData();
    }
  }, [canAccess]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // First, try just the statistics call
      console.log('📊 Fetching statistics...');
      const statsRes = await workOrdersApi.getStatistics();
      setWorkOrderStats(statsRes.data);
      console.log('✅ Statistics loaded successfully');

      // Then try analytics (optional - if it fails, dashboard still works)
      try {
        console.log('📈 Fetching analytics...');
        const analyticsRes = await workOrdersApi.getDetailedAnalytics({ timeRange: '30' });
        setAnalytics(analyticsRes.data);
        console.log('✅ Analytics loaded successfully');
      } catch (analyticsError) {
        console.warn('⚠️ Analytics failed to load:', analyticsError);
        // Analytics failure doesn't break the whole dashboard
        setAnalytics(null);
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('❌ Failed to fetch dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const getCompletionRate = () => {
    if (!workOrderStats) return 0;
    const total = parseInt(workOrderStats.total_work_orders) || 0;
    const completed = parseInt(workOrderStats.completed_count) || 0;
    return total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
  };

  const getOverdueTrend = () => {
    const overdue = parseInt(workOrderStats?.overdue_count) || 0;
    if (overdue === 0) return <TrendingUpIcon color="success" />;
    if (overdue <= 5) return <WarningIcon color="warning" />;
    return <WarningIcon color="error" />;
  };

  // Check access after all hooks are defined
  if (!canAccess(['manager'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only managers can access the management dashboard.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} sx={{ mr: 2 }} />
        <Typography variant="body1">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DashboardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Management Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overview of work orders, contractors, and system performance
            </Typography>
          </Box>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Key Metrics Cards */}
      {workOrderStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Work Orders"
              value={(parseInt(workOrderStats.pending_count) || 0) + (parseInt(workOrderStats.assigned_count) || 0) + (parseInt(workOrderStats.in_progress_count) || 0)}
              subtitle="In pipeline"
              icon={<AssignmentIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Completion Rate"
              value={`${getCompletionRate()}%`}
              subtitle="All time"
              icon={<CheckCircleIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Overdue Items"
              value={workOrderStats.overdue_count || 0}
              subtitle="Need attention"
              icon={<WarningIcon />}
              color="warning"
              trend={getOverdueTrend()}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Quality"
              value={workOrderStats.avg_quality_rating ? workOrderStats.avg_quality_rating.toFixed(1) : 'N/A'}
              subtitle="Star rating"
              icon={<TrendingUpIcon />}
              color="primary"
            />
          </Grid>
        </Grid>
      )}

      {/* Dashboard Tabs */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<DashboardIcon />} label="Overview" />
          <Tab icon={<BusinessIcon />} label="Contractors" />
          <Tab icon={<TimelineIcon />} label="Activity" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Work Order Distribution */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardHeader
                  title="Work Order Status Distribution"
                  action={
                    <Button
                      size="small"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate('/work-orders/analytics')}
                    >
                      View Full Analytics
                    </Button>
                  }
                />
                <CardContent>
                  {workOrderStats ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h6" color="warning.main">
                            {workOrderStats.pending_count}
                          </Typography>
                          <Typography variant="body2">Pending</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h6" color="info.main">
                            {workOrderStats.assigned_count}
                          </Typography>
                          <Typography variant="body2">Assigned</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h6" color="primary.main">
                            {workOrderStats.in_progress_count}
                          </Typography>
                          <Typography variant="body2">In Progress</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h6" color="success.main">
                            {workOrderStats.completed_count}
                          </Typography>
                          <Typography variant="body2">Completed</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">No data available</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Quick Actions" />
                <CardContent>
                  <Stack spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AssignmentIcon />}
                      onClick={() => navigate('/work-orders')}
                    >
                      Manage Work Orders
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<BusinessIcon />}
                      onClick={() => navigate('/contractors')}
                    >
                      Manage Contractors
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate('/work-orders/analytics')}
                    >
                      View Analytics
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ContractorPerformanceWidget
            maxItems={10}
            showHeader={false}
            onViewDetails={(contractor) => navigate(`/contractors`)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <WorkOrderTimelineWidget
            maxItems={20}
            showHeader={false}
          />
        </TabPanel>
      </Paper>

      {/* Floating Action Button for Quick Refresh */}
      <Fab
        color="primary"
        aria-label="refresh"
        onClick={handleRefresh}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <RefreshIcon />
      </Fab>
    </Box>
  );
};

export default ManagerDashboard; 